import React, {
  ReactNode,
  RefObject,
  useRef,
  useMemo,
  useCallback,
  useEffect,
  useState,
} from 'react'
import parse from 'autosuggest-highlight/parse'
import classNames from 'classnames'
import { TextInput, TextInputIconType } from 'components/atoms/TextInput'
import { orderBy as lodashOrderBy, get } from 'lodash'
import { Spinner } from 'transfix-ui/components/Spinner'
import {
  useAutocomplete,
  AutocompleteRenderGroupParams,
  AutocompleteRenderOptionState,
  AutocompleteFreeSoloValueMapping,
  Value,
  AutocompleteChangeReason,
  UseAutocompleteProps,
  AutocompleteChangeDetails,
  AutocompleteInputChangeReason,
  FilterOptionsState,
} from 'transfix-ui/core'
import { match } from 'utils/autocomplete/match'
import { v4 as uuid } from 'uuid'
import 'assets/scss/5_components/_dpl-autocomplete-hook.scss'

export const autocompleteChangeReasons: Record<string, AutocompleteChangeReason> = {
  createOption: 'createOption',
  selectOption: 'selectOption',
  removeOption: 'removeOption',
  clear: 'clear',
  blur: 'blur',
}

const simpleEmptyState = () => <span className='text-secondary'>Sorry, there are no results.</span>

/**
 * Returns ReactNode with highlighted text
 * @param {string} suggestion
 * @param {string} value
 */
export const getHighlightedMarkup = (suggestion: string, value: string): ReactNode => {
  const matches = match(suggestion, value, { requireMatchAll: true })
  const parts = parse(suggestion, matches)

  return (
    <span>
      {parts.map((part, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <span className={part.highlight ? 'b' : ''} key={`${i}-${part.text}`}>
          {part.text}
        </span>
      ))}
    </span>
  )
}

/**
 * For generated `getClearProps()` method provided
 * by output of `useAutocomplete`
 */
interface IClearProps {
  onClick: () => void
}

/**
 * For generated `getInputProps()` method generated
 * by output of `useAutocomplete`
 */
interface IInputProps {
  id: string
  value: any
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void
  onFocus: (e: React.FocusEvent<HTMLInputElement>) => void
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onMouseDown: (e: React.MouseEvent<HTMLInputElement>) => void
  // if open then this is handled imperativeley so don't let react override
  // only have an opinion about this when closed
  'aria-activedescendant'?: string
  'aria-autocomplete'?: 'none' | 'list' | 'inline' | 'both' | undefined
  'aria-controls'?: string
  // Disable browser's suggestion that might overlap with the popup.
  // Handle autocomplete but not autofill.
  autoComplete: string
  ref: RefObject<any>
  autoCapitalize: string
  spellCheck: boolean | 'true' | 'false' | undefined
}

/**
 * Access a value in an object by its key.
 * Default value is key itself
 */
export const getOptionGroupName = (
  groupNameKey: string = '',
  groupNamesByKey?: { [key: string]: string }
): string => {
  return get(groupNamesByKey, groupNameKey, groupNameKey)
}

interface IOptionGroupProps<T = any> {
  group: string
  index: number
  key: string
  options: T[]
}

/**
 * A typeguard function, which helps use a single object
 * in different type scenarios
 */
export function isOptionGroup<T>(arg: any): arg is IOptionGroupProps<T> {
  return typeof arg.group === 'string'
}

interface IAutoCompleteProps<
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined
> extends UseAutocompleteProps<T, Multiple, DisableClearable, FreeSolo> {
  id?: string
  dataTest?: string
  error?: string
  label?: string
  listbox?: string
  placeholder?: string
  textBoxID?: string
  name?: string
  disabled?: boolean
  required?: boolean
  large?: boolean
  clearable?: boolean
  autoFocus?: boolean
  /**
   * Callback fired when the value changes.
   *
   * @param {object} event The event source of the callback.
   * @param {T|T[]} value The new value of the component.
   * @param {string} reason One of "create-option", "select-option", "remove-option", "blur" or "clear".
   */
  onChange?: (
    event: React.ChangeEvent<{}>,
    value: string | T | NonNullable<T> | (string | T)[] | null,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<T>
  ) => void
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  onClearButtonClick?: Function
  renderEmptyState?: () => ReactNode
  icon?: TextInputIconType
  openOnFocus?: boolean | undefined
  /**
   * If `true`, the Autocomplete is free solo, meaning that the user input is not bound to provided options.
   */
  freeSolo?: FreeSolo
  /**
   * Array of options.
   */
  options: T[]
  /**
   * The value of the autocomplete.
   *
   * The value must have reference equality with the option in order to be selected.
   * You can customize the equality behavior with the `isOptionEqualToValue` prop.
   */
  value?: Value<T, Multiple, DisableClearable, FreeSolo>
  /**
   * Default value is used in instances where this component is uncontrolled.
   */
  defaultValue?: Value<T, Multiple, DisableClearable, FreeSolo>
  groupBy?: (option: T) => string
  isLoading?: boolean
  /**
   * Callback fired when the input value changes.
   */
  onInputChange?: (
    event: React.ChangeEvent<{}>,
    value: string,
    reason: AutocompleteInputChangeReason
  ) => void
  /**
   * Used to determine if an option is selected, considering the current value.
   * Uses strict equality by default.
   *
   * @param {T} option The option to test.
   * @param {T} value The value to test against.
   * @returns {boolean}
   */
  isOptionEqualToValue?: (option: T, value: T) => boolean

  /**
   * When using `groupBy`, this allows you to store
   * a keyed object with display labels as values,
   * @example { driverId: 'Driver ID', driverName: 'Driver name' }
   */
  optionGroupNames?: {
    [key: string]: string
  }

  /**
   * Used to determine the string value for a given option.
   * It's used to fill the input (and the list box options if `renderOption` is not provided).
   *
   * Note: If `T` is an object type, then either `getOptionLabel` or
   * `renderOption` _must_ be present, otherwise the component will crash the
   * page.
   *
   * @param {T} option
   * @returns {string}
   */
  getOptionLabel?: (option: T | AutocompleteFreeSoloValueMapping<FreeSolo>) => string

  /**
   * Used to determine the disabled state for a given option.
   *
   * @param {T} option The option to test.
   * @returns {boolean}
   */
  getOptionDisabled?: (option: T) => boolean

  /**
   * If `true`, the input's text will be cleared on blur if no value is selected.
   *
   * Set to `true` if you want to help the user enter a new value.
   * Set to `false` if you want to help the user resume his search.
   */
  clearOnBlur?: boolean
  /**
   * If `true`, clear all values when the user presses escape and the popup is closed.
   */
  clearOnEscape?: boolean
  /**
   * If `true`, the highlight can move to the input.
   */
  includeInputInList?: boolean
  /**
   * Render the group.
   *
   * @param {AutocompleteRenderGroupParams} param The group to render.
   * @returns {ReactNode}
   */
  renderGroup?: (params: AutocompleteRenderGroupParams) => ReactNode
  /**
   * Render the option, use `getOptionLabel` by default.
   *
   * @param {T} option The option to render.
   * @param {object} state The state of the component.
   * @returns {ReactNode}
   */
  renderOption?: (
    option: T,
    state: AutocompleteRenderOptionState,

    // Added to accomodate `roger` UI pattern of highlighting
    // query text with bold style
    markup?: ReactNode
  ) => ReactNode
  /**
   * Control the popup` open state.
   */
  open?: boolean
  /**
   * A filter function that determines the options that are eligible.
   *
   * @param {T[]} options The options to render.
   * @param {object} state The state of the component.
   * @returns {T[]}
   */
  filterOptions?: (options: T[], state: FilterOptionsState<T>) => T[]
  /**
   * Toggle clear flag is used to detect whether we need to clear the autocomplete,
   * used with the filter.
   * Toggle this value from the parent component to clear the inputs.
   */
  toggleClear?: boolean
  /**
   * This flag indiciates if the Autocomplete is used within a
   * FilterAutocomplete component.
   */
  filterAutocomplete?: boolean
  /**
   * If `true`, hide the selected options from the list box.
   */
  filterSelectedOptions?: boolean
  /**
   * Add React Node to beginning of input area
   */
  startAdornment?: (option: T | null) => ReactNode
  /**
   * If `true`, the selected option becomes the value of the input
   * when the Autocomplete loses focus unless the user chooses
   * a different option or changes the character string in the input.
   */
  autoSelect?: boolean
  /**
   * If `true`, the first option is automatically highlighted.
   */
  autoHighlight?: boolean
  /**
   * Limit # of displayed options when a search query is entered
   * undefined indicates no limit
   * @default undefined
   */
  limitOptions?: number | undefined
  /**
   * If true, the input's text will be selected on focus.
   * It helps the user clear the selected value.
   * @default true
   */
  selectOnFocus?: boolean
  /**
   * Enables defining a custom function to parse `inputProps.value`
   * and select a substring to display in `<TextInput />`
   * @default undefined
   */
  parseInputValue?: (val: string) => string
  /**
   * Highlights the field with a background color.
   * @default false
   */
  highlighted?: boolean
}

// typescript gymnastics to allow the defined values instead of forcing undefined by not including it.
type IAutoCompletePropsCustom = IAutoCompleteProps<any, boolean, boolean, boolean>
// path of least resistance to avoid type issues within the render component
type T = IAutoCompletePropsCustom

/** @deprecated Create an abstraction of <AutocompleteBase /> instead */
export const Autocomplete = React.forwardRef<HTMLDivElement, IAutoCompletePropsCustom>(
  function Autocomplete(
    {
      id,
      dataTest,
      error,
      label,
      onChange,
      onFocus,
      onBlur,
      placeholder,
      name,
      icon,
      autoFocus,
      required = false,
      disabled = false,
      large = false,
      clearable = false,
      options = [],
      getOptionLabel,
      getOptionDisabled,
      clearOnBlur = false,
      clearOnEscape = true,
      openOnFocus = true,
      includeInputInList = false,
      freeSolo,
      groupBy,
      isLoading,
      onInputChange,
      defaultValue: autocompleteDefaultValue,
      value: autocompleteValue,
      isOptionEqualToValue,
      optionGroupNames,
      renderGroup: renderGroupProp,
      renderOption: renderOptionProp,
      open,
      filterOptions,
      filterSelectedOptions,
      filterAutocomplete,
      startAdornment: startAdornmentProp,
      autoSelect = true,
      autoHighlight = true,
      toggleClear,
      limitOptions,
      selectOnFocus = true,
      parseInputValue: parseInputValueProp,
      highlighted = false,
      onClearButtonClick: onClearButtonClickProp,
      renderEmptyState = simpleEmptyState,
    },
    ref
  ) {
    const [textInputVal, setTextInputVal] = useState('')
    /**
     * Store a static, mutable ID using useRef
     */
    const generatedId = useRef(`autocomplete-hook-dpl-${uuid()}`)

    const getOptions = useCallback(() => {
      if (groupBy) {
        return lodashOrderBy(options, groupBy)
      }

      return options
    }, [groupBy, options])

    const sortedOptions = useMemo(() => getOptions(), [getOptions])

    const {
      getClearProps,
      getInputProps,
      getListboxProps,
      getOptionProps,
      getRootProps,
      groupedOptions,
      inputValue,
      popupOpen,
      value: currentValue,
    } = useAutocomplete({
      id: id || generatedId.current,
      clearOnEscape,
      clearOnBlur,
      openOnFocus,
      includeInputInList,
      getOptionLabel,
      getOptionDisabled,
      freeSolo,
      options: sortedOptions,
      groupBy,
      isOptionEqualToValue,
      onInputChange,
      open,
      value: autocompleteValue,
      defaultValue: autocompleteDefaultValue,
      filterOptions,
      filterSelectedOptions,
      autoSelect,
      autoHighlight,
      selectOnFocus,
      onChange: (
        event: React.ChangeEvent<{}>,
        val: string | T | NonNullable<T> | (string | T)[] | null,
        reason: AutocompleteChangeReason,
        details?: AutocompleteChangeDetails<T>
      ) => {
        if (onChange) {
          onChange(event, val, reason, details)
        }
      },
    })

    const clearProps = getClearProps() as IClearProps

    // Updating the value of `toggleClear` will clear the
    // Autocomplete if used within a filter
    useEffect(() => {
      if (filterAutocomplete) {
        clearProps.onClick()
      }
    }, [toggleClear])

    const innerRef = useRef<HTMLDivElement>(null)

    // Remove the data-focus element on first load, any selected item,
    // will now show as selected, instead of focused
    useEffect(() => {
      if (innerRef && innerRef.current) {
        // eslint-disable-next-line no-unused-expressions
        innerRef.current
          .getElementsByClassName('autocomplete-selected')
          .item(0)
          ?.removeAttribute('data-focus')
      }
    }, [groupedOptions])

    const {
      onBlur: onBlurProp,
      onChange: onInputChangeProp,
      onFocus: onFocusProp,
      ...inputProps
    } = getInputProps() as IInputProps

    const focusHandler = (e: React.FocusEvent<HTMLInputElement>) => {
      onFocusProp(e)

      if (onFocus) {
        onFocus(e)
      }
    }

    const blurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
      onBlurProp(e)

      if (onBlur) {
        onBlur(e)
      }
    }

    const inputChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
      setTextInputVal(e.target.value)
      onInputChangeProp(e)
    }

    const renderListOption = (option: T, index: number): ReactNode => {
      const optionProps = getOptionProps({ option, index })
      const optionLabel = getOptionLabel ? getOptionLabel(option) : ''
      const markup = getHighlightedMarkup(optionLabel, inputValue)

      // If there's a custom option component
      const optionContent = renderOptionProp
        ? renderOptionProp(
            option,
            {
              selected: !!optionProps['aria-selected'] as boolean,
              inputValue,
              index,
            },
            markup
          )
        : markup

      const { className = '' } = (option || {}) as any
      const classes = classNames('autocomplete-option', className)

      return (
        <li
          {...optionProps}
          className={classes}
          data-test={`${dataTest}-autocomplete-option-${index}`}
          key={index}>
          {optionContent}
        </li>
      )
    }

    /**
     * Render an option group, or pass in a custom render function
     */
    const renderGroup = (option: {
      key: string
      group: string
      index: number
      options: T[]
    }): ReactNode => {
      // limit the search options if a user has typed into the search bar
      const searchValuePresent = textInputVal !== ''
      const optionsList =
        limitOptions && searchValuePresent && option.options
          ? option.options.slice(0, limitOptions)
          : option.options

      if (renderGroupProp) {
        return renderGroupProp({
          key: option.key,
          group: option.group,

          // Index needs to be calculated as sum of base (group) index
          // and option index: i.e. `option.index + i`
          children: optionsList.map((o, i) => renderListOption(o, option.index + i)),
        })
      }

      return (
        <div key={option.key} className='autocomplete-group' role='group' aria-label={option.group}>
          <p className='autocomplete-group-label'>
            {getOptionGroupName(option.group, optionGroupNames)}
          </p>
          {optionsList.map((o, i) => renderListOption(o, option.index + i))}
        </div>
      )
    }

    const startAdornment = useMemo(() => startAdornmentProp && startAdornmentProp(currentValue), [
      currentValue,
      startAdornmentProp,
    ])

    const clearButtonClickHandler = useCallback(() => {
      clearProps.onClick()
      if (onClearButtonClickProp) onClearButtonClickProp()
    }, [clearProps, onClearButtonClickProp])

    /**
     * Separately computing `inputProps.value` allows us to define a
     * function to return a substring `inputProps.value` to display in the input
     */
    const { value: v = '', ...otherInputProps } = inputProps || {}
    const value = parseInputValueProp ? parseInputValueProp(v) : v

    return (
      <div
        ref={ref}
        {...getRootProps()}
        data-test={`${dataTest}-container`}
        className={classNames('dpl-autocomplete-hook', {
          'popup-open': popupOpen,
          loading: isLoading,
          'dpl-autocomplete-hook-hasResults': !!groupedOptions?.length,
        })}>
        <TextInput
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...otherInputProps}
          value={value}
          autoFocus={autoFocus}
          onFocus={focusHandler}
          onBlur={blurHandler}
          onChange={inputChangeHandler}
          autoComplete='off'
          large={large}
          name={name}
          disabled={disabled}
          validationMessage={error}
          invalid={!!error}
          label={label}
          required={required}
          icon={icon}
          clearable={clearable}
          placeholder={placeholder}
          dataTest={dataTest}
          onClearButtonClick={clearButtonClickHandler}
          startAdornment={startAdornment}
          className={classNames({ 'autocomplete-hook-filter': filterAutocomplete })}
          highlighted={highlighted ? 'info' : undefined}
        />

        <div ref={innerRef}>
          {isLoading ? (
            <ul
              {...getListboxProps()}
              className='search-results-container autocomplete-listbox loading'>
              <Spinner />
            </ul>
          ) : (
            ''
          )}

          {!isLoading && groupedOptions.length > 0 ? (
            <ul
              {...getListboxProps()}
              data-test={`${dataTest}-listbox`}
              className={classNames('search-results-container', {
                'autocomplete-listbox': !filterAutocomplete,
                'filter-listbox-results': filterAutocomplete,
              })}>
              {groupedOptions.map((option, index) => {
                if (isOptionGroup<T>(option)) {
                  return renderGroup(option)
                }

                return renderListOption(option as T, index)
              })}
            </ul>
          ) : (
            <>
              {filterAutocomplete && !isLoading && textInputVal && !groupedOptions.length && (
                <div className='pv4 ph2'>{renderEmptyState()}</div>
              )}
              {filterAutocomplete && isLoading && textInputVal && <Spinner position='relative' />}
            </>
          )}
        </div>
      </div>
    )
  }
)

export default Autocomplete
